<script setup lang="ts">
import { api } from '@/api';
import { EntityWithCommentType } from '@/api/generator';
import Comments from '@/components/comments/Comments.vue';
import FormSection from '@/components/forms/FormSection/FormSection.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import HistoryEvents from '@/components/history/HistoryEvents.vue';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import { defaultPageTabs } from '@/components/pages/BasePage/defaults';
import { type TabDefinition } from '@/components/Tabs/types.ts';
import { useTranslation } from 'i18next-vue';
import { computed } from 'vue';
import JobExecutionAdministrativeForm from './JobExecutionAdministrativeForm.vue';
import JobExecutionWorkListForm from './JobExecutionWorkListForm.vue';
import { useJobOccurrence } from '../utils/useJobOccurrence';
import { useAmountComments } from '@/components/comments/useAmountComments';
import { PageAction } from '@/components/pages/BasePage/types';
import { getDefaultActions, usePossibleActions } from '@/utils/actions';
import { linkExternal } from '@/components/icons/icons.ts';
import { mdiDownload } from '@mdi/js';
import { useRouter } from 'vue-router';
import { routenames } from '@/router/routenames';
import { deleteJobOccurrence } from '../utils/jobOccurrenceActions';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';

const { t } = useTranslation();

const router = useRouter();

const mode = useFormMode();

const { jobOccurrence, targetSystem } = useJobOccurrence();

const { commentsTabTitle, commentsChanged, amountComments } = useAmountComments(jobOccurrence);

interface JobExecutionFormProps {
  id: string;
}

const props = defineProps<JobExecutionFormProps>();

const { action: deleteAction } = useActionWithConfirmation({
  confirmedAction: async () => {
    const success = await deleteJobOccurrence({
      ...jobOccurrence.value,
      id: jobOccurrence.value.id ?? '',
    });
    if (success) {
      router.push({ name: routenames.jobOccurrences.overview });
    }
  },
  confirmationOptions: getDefaultDeleteConfirmationOptions(
    {
      id: jobOccurrence.value.id ?? '',
      name: jobOccurrence.value.jobConfigurationName ?? '',
      typeName: 'Job_one',
    },
    t,
  ),
});

const { enabled, visible } = usePossibleActions(jobOccurrence.value);

const actions = computed<PageAction[]>(() => {
  const defaultPageActions = getDefaultActions(mode.value);
  return [
    {
      get text() {
        return t('OpenJobInNewTab');
      },
      icon: linkExternal,
      callback: () => {
        window.open(window.location.href, '_blank');
      },
    },
    {
      get text() {
        return t('DownloadOrPrintReport');
      },
      icon: mdiDownload,
      callback: () => {
        const printUrl = router.resolve({
          name: routenames.jobOccurrences.report,
          params: { id: props.id },
        }).href;
        window.open(printUrl, '_blank');
      },
    },
    {
      ...defaultPageActions.delete,
      disabled: !enabled('Delete'),
      hidden: !visible('Delete'),
      callback: async () => {
        const defaultConfirmationOptions = getDefaultDeleteConfirmationOptions(
          {
            id: jobOccurrence.value.id || '',
            name: jobOccurrence.value.jobConfigurationName ?? '',
            typeName: 'Job_one',
            iuid: jobOccurrence.value.jobConfigurationIuid,
          },
          t,
        );
        deleteAction({
          param: jobOccurrence.value,
          confirmationOptions: {
            ...defaultConfirmationOptions,
            contentData: {
              ...defaultConfirmationOptions.contentData,
              hint: t('DeleteJobOccurrenceHintMsg', {
                hint: defaultConfirmationOptions.contentData.hint,
              }),
            },
          },
        });
      },
    },
  ];
});

const tabs = computed(
  () =>
    ({
      administrative: {
        ...defaultPageTabs.administrative,
      },
      workitems: { title: t('WorkList'), hideContentSheet: true },
      comments: {
        ...defaultPageTabs.comments,
        hidden: mode.value === 'add',
        title: commentsTabTitle.value,
      },
      history: {
        ...defaultPageTabs.history,
        hidden: mode.value === 'add',
        unmountTab: true,
      },
    }) satisfies TabDefinition,
);
</script>

<template>
  <BasePageNavigation :tabs="tabs" :actions="actions">
    <template #administrative>
      <JobExecutionAdministrativeForm />
    </template>

    <template #workitems>
      <JobExecutionWorkListForm :targetSystem="targetSystem" />
    </template>

    <template #comments>
      <FormSection>
        <Comments
          v-if="jobOccurrence.id"
          :id="jobOccurrence.id"
          :key="amountComments"
          :entityWithCommentType="EntityWithCommentType.JobOccurrence"
          @change="commentsChanged"
        />
      </FormSection>
    </template>

    <template #history>
      <HistoryEvents
        v-if="jobOccurrence.id"
        :id="jobOccurrence.id"
        :historyApiFn="api.jobOccurrencesApi.getJobOccurrenceHistory.bind(api.jobOccurrencesApi)"
      />
    </template>
  </BasePageNavigation>
</template>
